/**
 * Input/response contracts for the getSurveyResults callable (ADR-054).
 *
 * Returns server-computed aggregates for a survey. Individual response
 * docs never leave the server — the callable reads them via Admin SDK
 * and returns only aggregate counts/averages. This preserves the
 * anonymous guarantee: personId is stored for dedup but never exposed.
 *
 * Multi-question: results are returned as a parallel `questions[]` array
 * matching `Survey.questions[]` order. Each entry holds its own typed
 * aggregate. `totalRespondents` counts response docs (one per person);
 * each question's `totalResponses` counts non-null answers for that
 * question.
 */

import { z } from 'zod';

export const getSurveyResultsInputSchema = z
  .object({
    surveyId: z.string().min(1),
  })
  .strict();

export type GetSurveyResultsInput = z.infer<typeof getSurveyResultsInputSchema>;

export interface ChoiceOptionResult {
  label: string;
  count: number;
  pct: number;
}

export interface ChoiceResults {
  type: 'single_choice';
  totalResponses: number;
  options: ChoiceOptionResult[];
}

export interface RatingDistribution {
  /** Star value (1–5). */
  star: 1 | 2 | 3 | 4 | 5;
  count: number;
  pct: number;
}

export interface RatingResults {
  type: 'rating';
  totalResponses: number;
  average: number;
  /** Per-star breakdown. Stars 1..5, always present even when count = 0. */
  distribution: RatingDistribution[];
}

export interface FreeTextResults {
  type: 'free_text';
  totalResponses: number;
  /**
   * All free-text responses, server-shuffled to break submission-order
   * correlation. Empty/null responses are filtered out. personId is never
   * included — anonymity is preserved by the callable, not the caller.
   */
  responses: string[];
}

export type SurveyAggregateResults = ChoiceResults | RatingResults | FreeTextResults;

export interface QuestionResults {
  questionId: string;
  prompt: string;
  results: SurveyAggregateResults;
}

export interface GetSurveyResultsResponse {
  /** Per-question aggregates, in the same order as `Survey.questions[]`. */
  questions: QuestionResults[];
  /** Count of response docs (one per person). */
  totalRespondents: number;
}
